import * as THREE from 'three';
/* ============================================================
   程序貼圖的共用底座
   ============================================================

   `vfx-tex.ts`（第三版實彈風格）自己有一份同樣的 make/put，
   這裡刻意**不去動那個檔** —— 它是現行遊戲正在用的東西，而這一輪是
   在旁邊長出三個候選風格。等使用者挑定，再把落選的連同重複的底座一起收掉。

   共用的只有三件事：畫布建立、alpha 寫入、固定偽隨機。 */

var HAS_DOC = typeof document !== 'undefined' && typeof document.createElement === 'function';
var cache: Record<string, THREE.Texture | null> = {};

/** 建一張程序貼圖（同一個 key 只生成一次）。headless 回 `null`，
    由 `vfx-pool.ts` 的 READY 統一擋掉所有 spawn。 */
export function mkTex(key: string, w: number, h: number,
  draw: (d: Uint8ClampedArray, w: number, h: number) => void) {
  if (key in cache) return cache[key];
  if (!HAS_DOC) { cache[key] = null; return null; }
  var c = document.createElement('canvas'); c.width = w; c.height = h;
  var g = c.getContext('2d'); if (!g) { cache[key] = null; return null; }
  var img = g.createImageData(w, h);
  draw(img.data, w, h);
  g.putImageData(img, 0, 0);
  var t = new THREE.CanvasTexture(c);
  /* three r128 沒有 ColorManagement：不標 sRGB 的話 `outputEncoding` 會把
     畫布上的濃淡提亮近兩倍（2026-08-04 教訓），硬邊風格的平塗色階會整個垮掉。 */
  (t as any).encoding = (THREE as any).sRGBEncoding;
  t.needsUpdate = true;
  cache[key] = t;
  return t;
}

/** 白色 + alpha。顏色一律由材質的 `color` 決定，貼圖只負責形狀。 */
export function putA(d: Uint8ClampedArray, i: number, a: number) {
  d[i] = d[i + 1] = d[i + 2] = 255;
  d[i + 3] = (Math.max(0, Math.min(1, a)) * 255) | 0;
}

/** 硬邊的一像素抗鋸齒：`inside` 是「到邊界的距離」（正值在內側）。

    **這是整個硬邊風格的關鍵函式。** 直接用 `inside > 0 ? 1 : 0` 會在
    放大到十幾公尺的特效上出現階梯狀鋸齒（貼圖只有 128 px，放大 30 倍
    等於一格 4 個螢幕像素）；而用漸層過渡又會糊回柔邊。
    只給一格的過渡寬度：邊緣仍然銳利，但不會有鋸齒。 */
export function edge(inside: number, aa: number) {
  return Math.max(0, Math.min(1, inside / aa + 0.5));
}

/** 固定偽隨機。貼圖只生成一次，用 `Math.random` 的話每次開遊戲形狀都不一樣。 */
export function hash(n: number) {
  var s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

/** 正 n 邊形在角度 θ 上的半徑（外接半徑 = 1）。 */
export function polyR(n: number, th: number) {
  var seg = Math.PI * 2 / n;
  var a = th - Math.floor(th / seg) * seg - seg / 2;
  return Math.cos(Math.PI / n) / Math.cos(a);
}
